/**
 * Chart color utilities - ensures no color repetition when there are more
 * series/breakdowns than theme colors. Uses theme colors first, then distinct
 * extended colors, then generated HSL colors for very large counts.
 */

// Extended palette: distinct colors for when theme colors are exhausted.
const EXTENDED_PALETTE = [
  "#0ea5e9", "#84cc16", "#f43f5e", "#8b5cf6", "#06b6d4", "#eab308",
  "var(--feedback-success)", "#ec4899", "var(--feedback-info)", "#14b8a6", "#f97316", "#a855f7",
  "#2e1065", "#166534", "#9a3412", "#4f46e5", "#0d9488", "#be185d",
  "var(--feedback-info)", "var(--feedback-success)", "#b45309", "#7e22ce", "#0369a1", "#15803d",
  "#c2410c", "#6d28d9", "#0e7490", "#065f46", "#9f1239", "#4c1d95",
  "#1e3a8a", "#14532d", "#713f12", "#5b21b6", "#155e75", "#134e4a",
  "#881337", "#581c87", "#0c4a6e", "#164e63",
];

function hslToHex(h, s, l) {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Returns a unique color for the given index. Uses base palette (theme colors)
 * first, then EXTENDED_PALETTE, then generates distinct HSL colors - never repeats.
 *
 * @param {number} index - Zero-based series/breakdown index
 * @param {string[]} basePalette - Theme or config colors (e.g. from chart config)
 * @returns {string} Hex color
 */
export function getSeriesColor(index, basePalette = null) {
  const palette = basePalette && Array.isArray(basePalette) && basePalette.length > 0
    ? basePalette
    : null;

  if (palette && index < palette.length) {
    return palette[index];
  }

  const baseCount = palette ? palette.length : 0;
  const extendedIndex = index - baseCount;

  if (extendedIndex < EXTENDED_PALETTE.length) {
    return EXTENDED_PALETTE[extendedIndex];
  }

  // Generate deterministic distinct hue (137° step gives good spread)
  const hue = (137 * extendedIndex) % 360;
  return hslToHex(hue, 65, 52);
}
